// ─── Resilient signed-URL uploader ───────────────────────────────────────────
// PUTs a file to a Supabase storage signed URL with a stall watchdog + retry.
//
// Why this exists: a plain XMLHttpRequest PUT has no timeout, so on a flaky
// mobile connection (Android cellular, Google-Photos-sourced files) the request
// can establish and then silently stop transmitting — the upload "hangs"
// forever, the concurrency slot that owns it never frees, and once every slot
// is stuck the whole batch appears to STOP with no error. Customers reported
// exactly this. We fix it at the source:
//   1. A stall watchdog aborts an attempt that goes `stallMs` with zero upload
//      progress (idle-based, NOT a hard total timeout — so large files on slow
//      links are fine as long as bytes keep flowing).
//   2. Transient failures (stall / network / 5xx / 429 / 408) are retried with
//      backoff. PUT to a signed URL with `x-upsert: true` is idempotent, so a
//      re-send simply overwrites — safe to retry.
//   3. Permanent failures (4xx other than 408/429) are NOT retried.

import { uploadContentType } from './uploadContentType';

export interface UploadOptions {
  /** Abort + retry an attempt that goes this many ms with no upload progress. */
  stallMs?: number;
  /** Retry attempts after the first try. `2` ⇒ up to 3 total attempts. */
  maxRetries?: number;
}

// 15s (was 30s) — recover from a genuinely stalled PUT in half the time so a
// flaky photo doesn't freeze the counter for 30s. The watchdog resets on ANY
// upload progress, so this only fires on a true 15s-of-nothing stall.
const DEFAULT_STALL_MS = 15_000;

/**
 * PRINT PDFs (2026-10-09): one file is 10–200 MB. Before the first byte
 * leaves, the browser copies the whole blob to its network process — on a
 * laptop that has just built the PDF that can take well over 15 s with no
 * progress, and « Régénérer » failed with « Upload stalled » on every order.
 * Two minutes of silence, and one more try, before giving up.
 */
export const PDF_UPLOAD: UploadOptions = { stallMs: 120_000, maxRetries: 3 };
const DEFAULT_MAX_RETRIES = 2;

type UploadError = Error & { status?: number; code?: 'STALL' | 'NETWORK' };

function isRetryable(err: UploadError): boolean {
  if (err.code === 'STALL' || err.code === 'NETWORK') return true;
  if (typeof err.status === 'number') {
    return err.status >= 500 || err.status === 429 || err.status === 408;
  }
  return false;
}

function attemptUpload(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: ((percent: number) => void) | undefined,
  stallMs: number,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('x-upsert', 'true');

    let settled = false;
    // Stall watchdog: abort once `stallMs` elapses with no upload progress.
    // Compares actual elapsed time since the last progress event (vitest fakes
    // Date.now() in lockstep with timers, so this stays deterministic in tests)
    // — quantized tick-accumulation could overshoot and abort a live upload.
    // We poll on a cheap interval (≤5s) rather than a single hard timeout so
    // large files on slow links never trip as long as bytes keep flowing.
    let lastProgressAt = Date.now();
    const pollEvery = Math.min(stallMs, 5000);
    let watchdog: ReturnType<typeof setInterval> | null = setInterval(() => {
      if (Date.now() - lastProgressAt >= stallMs) {
        const e: UploadError = Object.assign(new Error('Upload stalled'), { code: 'STALL' as const });
        finish(() => reject(e), () => { try { xhr.abort(); } catch { /* ignore */ } });
      }
    }, pollEvery);

    const clearWatchdog = () => { if (watchdog) { clearInterval(watchdog); watchdog = null; } };
    const finish = (settleFn: () => void, sideEffect?: () => void) => {
      if (settled) return;
      settled = true;
      clearWatchdog();
      sideEffect?.();
      settleFn();
    };
    const markProgress = () => { lastProgressAt = Date.now(); };

    xhr.upload.addEventListener('loadstart', markProgress);
    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      markProgress();
      if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });

    xhr.addEventListener('load', () => {
      finish(() => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          // Surface Supabase's REAL reason instead of an opaque "HTTP 400".
          // Storage sends a JSON body, e.g. for an oversized file:
          //   {"statusCode":"413","error":"Payload too large",
          //    "message":"The object exceeded the maximum allowed size"}
          // A too-large PDF is user-meaningful (158-photo albums cross the
          // server's upload cap), so we translate it to a clear message and
          // include the file size. NOT retryable (a 4xx never gets smaller on
          // a re-send) — isRetryable already rejects <500, so it fails fast.
          let detail = '';
          let tooLarge = false;
          try {
            const body = JSON.parse(xhr.responseText || '{}');
            detail = (body && (body.message || body.error)) || '';
            if (xhr.status === 413 || /maximum allowed size|payload too large|exceeded/i.test(`${body?.error ?? ''} ${body?.message ?? ''}`)) {
              tooLarge = true;
            }
          } catch { /* body wasn't JSON — fall back to the status line */ }
          const mb = Math.round((file.size / 1048576) * 10) / 10;
          const msg = tooLarge
            ? `PDF too large (${mb} MB): it exceeds the server's upload size limit. Lower the photo count or contact support.`
            : `Upload failed: HTTP ${xhr.status}${detail ? ` — ${detail}` : ''}`;
          const e: UploadError = Object.assign(new Error(msg), { status: xhr.status });
          reject(e);
        }
      });
    });
    xhr.addEventListener('error', () => {
      const e: UploadError = Object.assign(new Error('Upload network error'), { code: 'NETWORK' as const });
      finish(() => reject(e));
    });
    // `abort` fires after the watchdog calls xhr.abort(); the watchdog already
    // settled the promise, so the guard in `finish` makes this a no-op.
    xhr.addEventListener('abort', () => {
      const e: UploadError = Object.assign(new Error('Upload aborted'), { code: 'NETWORK' as const });
      finish(() => reject(e));
    });

    xhr.send(file);
  });
}

export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  contentType?: string,
  onProgress?: (percent: number) => void,
  opts?: UploadOptions,
): Promise<void> {
  const stallMs = opts?.stallMs ?? DEFAULT_STALL_MS;
  const maxRetries = opts?.maxRetries ?? DEFAULT_MAX_RETRIES;
  // ⚠️ THE LABEL THE BUCKET ACCEPTS (2026-10-03): image/jpg, image/pjpeg and an
  // empty type are all valid JPEGs that a bucket allow-list refuses with a 400.
  const type = uploadContentType(contentType || file.type, file.name);

  let lastErr: UploadError | null = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      // Backoff: 1.5s, 3s, 4.5s … gives a flaky link time to recover before
      // re-sending. Reset the visible progress so the bar doesn't look stuck
      // at a partial percentage from the failed attempt.
      onProgress?.(0);
      await new Promise((r) => setTimeout(r, attempt * 1500));
    }
    try {
      await attemptUpload(uploadUrl, file, type, onProgress, stallMs);
      return;
    } catch (err) {
      lastErr = err as UploadError;
      if (!isRetryable(lastErr)) throw lastErr;
    }
  }
  throw lastErr ?? new Error('Upload failed');
}
